import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import { getAuth, signInWithCustomToken, type Auth } from "firebase/auth";
import {
  getFirestore,
  initializeFirestore,
  serverTimestamp,
  type Firestore,
} from "firebase/firestore";
import { getDatabase, type Database } from "firebase/database";
import { Capacitor } from "@capacitor/core";
import type { ChatFirebasePayload, ChatMessageDoc } from "@/types/expressChat";

const APP_NAME = "express-booking-chat";
const IST = "Asia/Kolkata";
export const ONLINE_WINDOW_MS = 10_000;
const DEFAULT_RTDB_URL =
  "https://production-462813-default-rtdb.asia-southeast1.firebasedatabase.app";

export type ExpressChatFirebaseClient = {
  app: FirebaseApp;
  auth: Auth;
  db: Firestore;
  rtdb: Database | null;
};

export const pathSegments = (path: string): string[] =>
  path.split("/").filter(Boolean);

export function firebaseDatabaseURL(fb: ChatFirebasePayload): string | undefined {
  const fromConfig = fb.config.databaseURL || fb.config.database_url;
  if (fromConfig) return fromConfig;
  if (fb.config.projectId === "production-462813") return DEFAULT_RTDB_URL;
  return undefined;
}

export function ownPresencePath(fb: ChatFirebasePayload): string {
  const raw = (fb.presence_path || "").replace(/^\/+/, "");
  if (raw) return raw;
  return `presence/${fb.uid}`;
}

export async function initFirebaseFromPayload(
  fb: ChatFirebasePayload,
): Promise<ExpressChatFirebaseClient> {
  const databaseURL = firebaseDatabaseURL(fb);
  const config = {
    ...fb.config,
    ...(databaseURL ? { databaseURL } : {}),
  };
  const existing = getApps().find((app) => app.name === APP_NAME);
  const app = existing ?? initializeApp(config, APP_NAME);
  const auth = getAuth(app);

  if (fb.custom_token && auth.currentUser?.uid !== fb.uid) {
    await signInWithCustomToken(auth, fb.custom_token);
  }
  await auth.authStateReady();

  // On native, force long-polling so Firestore uses the XHR transport that
  // installCapacitorFirebaseNetworkBypass() routes around CapacitorHttp.
  const db = existing
    ? getFirestore(app)
    : initializeFirestore(
        app,
        Capacitor.isNativePlatform()
          ? { experimentalForceLongPolling: true }
          : {},
      );
  let rtdb: Database | null = null;
  try {
    rtdb = databaseURL ? getDatabase(app, databaseURL) : getDatabase(app);
  } catch {
    try {
      rtdb = getDatabase(app);
    } catch {
      rtdb = null;
    }
  }

  return { app, auth, db, rtdb };
}

export function buildChatMessagePayload(
  firebase: ChatFirebasePayload,
  text: string,
  messageId: string,
) {
  const cleaned = text.trim();
  const role = firebase.role;
  const uid = firebase.uid;
  const senderIdRaw =
    role === "artist"
      ? String(uid.replace(/^artist_/, ""))
      : String(uid.replace(/^client_/, ""));

  return {
    messageId,
    senderId: uid,
    senderRole: role,
    sender_type: role,
    sender_id: senderIdRaw,
    text: cleaned,
    body: cleaned,
    type: "text" as const,
    attachments: [] as unknown[],
    isMasked: false,
    is_masked: false,
    createdAt: serverTimestamp(),
    created_at: serverTimestamp(),
    created_at_utc: true,
    readBy: [uid],
  };
}

export function parseChatDate(value: unknown): Date | null {
  if (
    value &&
    typeof value === "object" &&
    "toDate" in value &&
    typeof (value as { toDate: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === "number") {
    const ms = value < 1e12 ? value * 1000 : value;
    return new Date(ms);
  }
  if (typeof value === "string") {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return null;
}

const istParts = (date: Date) =>
  new Intl.DateTimeFormat("en-GB", {
    timeZone: IST,
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

export function formatChatTimestamp(createdAt: unknown): string {
  const date = parseChatDate(createdAt) ?? new Date();
  const parts = istParts(date);
  const hour = parts.find((part) => part.type === "hour")?.value ?? "";
  const minute = parts.find((part) => part.type === "minute")?.value ?? "";
  const dayPeriod = parts.find((part) => part.type === "dayPeriod")?.value ?? "";
  return `${hour}:${minute} ${dayPeriod}`.trim();
}

export function formatChatDateLabel(createdAt: unknown): string {
  const date = parseChatDate(createdAt) ?? new Date();
  const todayKey = istDateKey(new Date());
  const messageKey = istDateKey(date);
  if (messageKey === todayKey) return "TODAY";
  const parts = istParts(date);
  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const year = parts.find((part) => part.type === "year")?.value ?? "";
  return `${day} ${month} ${year}`.toUpperCase();
}

export function istDateKey(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export const CHAT_MASK_PLACEHOLDERS = {
  phone: "[PHONE NUMBER HIDDEN]",
  email: "[EMAIL HIDDEN]",
  upi: "[UPI HIDDEN]",
} as const;

const EXISTING_CHAT_MASK =
  /\[PHONE NUMBER HIDDEN\]|\[EMAIL HIDDEN\]|\[UPI HIDDEN\]/;

const INDIAN_PHONE_RE = /(?:\+91[\s-]?)?[6-9](?:[\s-]?\d){9}/g;
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
const UPI_RE =
  /[a-zA-Z0-9._-]{2,}@(?:paytm|ybl|oksbi|okicici|okhdfcbank|axl|ibl|upi)\b/gi;

export function maskChatPersonalInfo(text: string): {
  text: string;
  wasMasked: boolean;
} {
  if (!text) return { text: "", wasMasked: false };

  let result = text;
  let wasMasked = EXISTING_CHAT_MASK.test(text);

  const apply = (pattern: RegExp, placeholder: string) => {
    const next = result.replace(pattern, placeholder);
    if (next !== result) wasMasked = true;
    result = next;
  };

  apply(INDIAN_PHONE_RE, CHAT_MASK_PLACEHOLDERS.phone);
  apply(EMAIL_RE, CHAT_MASK_PLACEHOLDERS.email);
  apply(UPI_RE, CHAT_MASK_PLACEHOLDERS.upi);

  return { text: result, wasMasked };
}

export function getChatMessageDisplayText(text: string): string {
  return maskChatPersonalInfo(text).text;
}

export function mapChatMessageDoc(
  id: string,
  data: ChatMessageDoc,
  myUid: string,
) {
  const senderId =
    data.senderId ||
    (data.sender_id != null && (data.sender_type || data.senderRole)
      ? `${data.sender_type || data.senderRole}_${data.sender_id}`
      : "");
  const created = data.created_at ?? data.createdAt;
  const date = parseChatDate(created);
  const rawText = data.body || data.text || "";
  const masked = maskChatPersonalInfo(rawText);
  return {
    id,
    side: senderId === myUid ? ("outgoing" as const) : ("incoming" as const),
    text: masked.text,
    timestamp: formatChatTimestamp(created),
    dateLabel: formatChatDateLabel(created),
    createdAtMs: date?.getTime() ?? 0,
    isMasked: Boolean(data.isMasked || data.is_masked || masked.wasMasked),
    status: "sent" as const,
  };
}

export function typingUidFromField(field?: string | null): string | null {
  if (!field) return null;
  return field.replace(/^typing\./, "") || null;
}

export function typingFieldPath(
  field?: string | null,
  uid?: string | null,
): string | null {
  const raw = field || (uid ? `typing.${uid}` : null);
  if (!raw) return null;
  return raw.startsWith("typing.") ? raw : `typing.${raw}`;
}

export function otherParticipantUid(fb: ChatFirebasePayload): string | null {
  return (
    fb.other_participant_uid ||
    typingUidFromField(fb.other_typing_field) ||
    fb.participant_ids?.find((id) => id && id !== fb.uid) ||
    null
  );
}

export function otherPresencePath(fb: ChatFirebasePayload): string | null {
  if (fb.other_presence_path) return fb.other_presence_path;
  const otherUid = otherParticipantUid(fb);
  return otherUid ? `presence/${otherUid}` : null;
}

type PresenceValue = {
  online?: boolean;
  lastSeen?: unknown;
  last_seen_at?: unknown;
  last_seen?: unknown;
  lastSeenAt?: unknown;
} | null;

export function parsePresenceLastSeenMs(presence: PresenceValue): number | null {
  if (!presence) return null;
  const raw =
    presence.lastSeen ??
    presence.last_seen_at ??
    presence.last_seen ??
    presence.lastSeenAt;
  const date = parseChatDate(raw);
  return date ? date.getTime() : null;
}

export function isPresenceOnline(presence: PresenceValue): boolean {
  if (!presence) return false;
  if (presence.online === false) return false;

  const lastSeenMs = parsePresenceLastSeenMs(presence);
  if (lastSeenMs != null) {
    return Date.now() - lastSeenMs < ONLINE_WINDOW_MS;
  }
  return presence.online === true;
}

export function formatLastSeenLabel(lastSeenMs: number | null): string | null {
  if (lastSeenMs == null) return null;
  const delta = Math.max(0, Date.now() - lastSeenMs);
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return "Last seen just now";
  if (minutes < 60) return `Last seen ${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Last seen ${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `Last seen ${days}d ago`;
}
